import { ILeisureArea } from '@@sapporo/leisure/areas/ILeisureArea';
import { LeisureBrandInterface } from '@@sapporo/leisure/brand/LeisureBrandInterface';
import { ILeisureCategoryV2 } from '@@sapporo/leisure/categories/v2/interface/ILeisureCategoryV2';
import { IPostRecommendLeisuresResponse } from '@@sapporo/leisure/recommand/interface/response/IPostRecommendLeisuresResponse';
import { ILeisureSpecialEventDetail } from '@@sapporo/leisure/specialEvent/interface/ILeisureSpecialEventDetail';
import { ILeisureSpecialPriceList } from '@@sapporo/leisure/specialPrice/interface/ILeisureSpecialPriceList';
import { IDIRecommendWidget } from '@@sapporo/recommend/common/interface/IDIRecommendWidget';
import { ITicket } from '@@sapporo/ticket/interface/ITicket';
import debug from 'debug';

import { BaseActions } from '../Common/Actions/BaseActions';
import { execAction } from '../Common/Actions/handler';
import { ResponseEither } from '../Common/ResponseEither';
import { noContentAs404 } from '../Common/ResponseEither/transformers/noContentAs404';
import { noContentAsError } from '../Common/ResponseEither/transformers/noContentAsError';
import { ILeisureAreasRequestParams } from './interfaces/ILeisureAreasRequestParams';
import { ILeisureBrandDetailRequestParams } from './interfaces/ILeisureBrandDetailRequestParams';
import { ILeisureBrandListByCategoryRequestParams } from './interfaces/ILeisureBrandListByCategoryRequestParams';
import { ILeisureBrandListRequestParams } from './interfaces/ILeisureBrandListRequestParams';
import { ILeisureBrandTicketsRequestParams } from './interfaces/ILeisureBrandTicketsRequestParams';
import { ILeisureCategoriesRequestParams } from './interfaces/ILeisureCategoriesRequestParams';
import { ILeisureCrossSellListRequestParams } from './interfaces/ILeisureCrossSellListRequestParams';
import { ILeisureCrossSellWidgetRequestParams } from './interfaces/ILeisureCrossSellWidgetRequestParams';
import { ILeisureHotDealListRequestParams } from './interfaces/ILeisureHotdealRequestParams';
import { ILeisureMediumCategoriesRequestParams } from './interfaces/ILeisureMediumCategoriesRequestParams';
import { ILeisureNearbyRequestParams } from './interfaces/ILeisureNearbyRequestParams';
import { ILeisureRecentsRequestParams } from './interfaces/ILeisureRecentsRequestParams';
import { ILeisureRecommendRequestParams } from './interfaces/ILeisureRecommendRequestParams';
import { ILeisureSpecialEventDetailRequestParams } from './interfaces/ILeisureSpecialEventDetailRequestParams';
import { ILeisureTicketListRequestParams } from './interfaces/ILeisureTicketListRequestParams';
import { ILeisureGenericTicketsPayload } from './interfaces/payloads/ILeisureGenericTicketsPayload';
import { ILeisureHomeTitlePayload } from './interfaces/payloads/ILeisureHomeTitlePayload';
import { ILeisurePopularBrandListPayload } from './interfaces/payloads/ILeisurePopularBrandListPayload';
import { ILeisurePopularCategoriesPayload } from './interfaces/payloads/ILeisurePopularCategoriesPayload';
import { ILeisureCrossSellListPayload } from './interfaces/payloads/ILeisureRegionCrossSellListPayload';
import { ILeisureCrossSellWidgetPayload } from './interfaces/payloads/ILeisureRegionCrossSellWidgetPayload';
import { ILeisureSpecialEventListPayload } from './interfaces/payloads/ILeisureSpecialEventListPayload';
import { ILeisureHomeWidgetOrderPayload } from '@/models/Leisure/interfaces/payloads/ILeisureHomeWidgetOrderPayload';
import { LeisureRequestBuilder } from './LeisureRequestBuilder';
import { ILeisureProductsByIdsRequestParams } from '@/models/Leisure/interfaces/ILeisureProductsByIdsRequestParams';
import { ILeisureProductsPayload } from './interfaces/payloads/ILeisureProductsPayload';
import { ILeisureProductsV2 } from '@@achasan/leisure/search/interface/ILeisureProductsV2';

const log = debug('sunny:LeisureActions');
export type getProductsByIdsFn = (
  params: ILeisureProductsByIdsRequestParams,
) => Promise<ResponseEither<ILeisureProductsV2>>;
export class LeisureActions extends BaseActions<LeisureRequestBuilder> {
  /** 전체 지역 리스트 -- GET `/leisure/v3/areas` */
  areas(params?: ILeisureAreasRequestParams): Promise<ResponseEither<readonly ILeisureArea[]>> {
    log('Getting leisure areas');
    return execAction('leisure areas', this.requestBuilder.createGetAreasQuery(params), noContentAsError);
  }

  /** 전체 카테고리 리스트 V2 -- GET `/leisure/categories/v2` */
  categories(
    params?: ILeisureCategoriesRequestParams,
  ): Promise<ResponseEither<readonly ILeisureCategoryV2.ICategoryV2[]>> {
    log('Getting leisure categories v2');
    return execAction('leisure categories v2', this.requestBuilder.createGetCategoriesQuery(params), noContentAsError);
  }

  mediumCategories(
    params?: ILeisureMediumCategoriesRequestParams,
  ): Promise<ResponseEither<ILeisureCategoryV2.ICategoryV2>> {
    log('Getting leisure medium categories v2');
    return execAction(
      'leisure medium cateogries v2',
      this.requestBuilder.createGetMediumCategoriesQuery(params),
      noContentAsError,
    );
  }

  brandListByCategory(
    params: ILeisureBrandListByCategoryRequestParams,
  ): Promise<ResponseEither<LeisureBrandInterface.IDetail[]>> {
    log('Getting brand list by cateogry');
    return execAction('leisure brand list by category', this.requestBuilder.createGetBrandListByCategory(params));
  }

  /** 서브홈 대표 카테고리 목록 -- GET `/leisure/categories/v2/popular` */
  homePopularCategories(): Promise<ResponseEither<ILeisurePopularCategoriesPayload>> {
    log('Getting leisure home popular categories');
    return execAction(
      'leisure home popular categories',
      this.requestBuilder.createGetHomePopularCategoriesQuery(),
      noContentAsError,
    );
  }

  homeWidgetOrder(): Promise<ResponseEither<ILeisureHomeWidgetOrderPayload>> {
    log('Getting leisure home display order');
    return execAction(
      'leisure home display order',
      this.requestBuilder.createGetHomeWidgetOrderQuery(),
      noContentAsError,
    );
  }

  mdPicks(): Promise<ResponseEither<ILeisureGenericTicketsPayload>> {
    log('Getting leisure md picks');
    return execAction('leisure md picks', this.requestBuilder.createGetMDPicksQuery(), noContentAsError);
  }

  nearby(params: ILeisureNearbyRequestParams): Promise<ResponseEither<ILeisureGenericTicketsPayload>> {
    log('Getting nearby leisures');
    return execAction('leisure nearby', this.requestBuilder.createGetNearbyQuery(params), noContentAsError);
  }

  nearbyHotdeals(params: ILeisureNearbyRequestParams): Promise<ResponseEither<ILeisureGenericTicketsPayload>> {
    log('Getting nearby leisure hotdeals');
    return execAction(
      'leisure nearby hotdeals',
      this.requestBuilder.createGetNearbyHotdealsQuery(params),
      noContentAsError,
    );
  }

  popularRecommendations(): Promise<ResponseEither<IDIRecommendWidget>> {
    log('Getting popular recommendations');
    return execAction('leisure popular recommendations', this.requestBuilder.createGetPopularRecommendations());
  }

  recents(params?: ILeisureRecentsRequestParams): Promise<ResponseEither<ILeisureGenericTicketsPayload>> {
    log('Getting lesire recents');
    return execAction('leisure recents', this.requestBuilder.createGetRecentsQuery(params));
  }

  /** 추천 레저/티켓 */
  recommendList(params: ILeisureRecommendRequestParams): Promise<ResponseEither<IPostRecommendLeisuresResponse>> {
    log('Getting leisure recommend list');
    return execAction(
      'leisure recommend list',
      this.requestBuilder.createPostRecommendListQuery(params),
      noContentAs404,
    );
  }

  /** 쇼킹 - 핫딜리스트 */
  shockingList(params: ILeisureHotDealListRequestParams): Promise<ResponseEither<ILeisureSpecialPriceList>> {
    log('Getting leisure hotdeal list');
    return execAction('leisure shocking list', this.requestBuilder.createGetShockingHotDealQuery(params));
  }

  specialEventDetail(
    params: ILeisureSpecialEventDetailRequestParams,
  ): Promise<ResponseEither<ILeisureSpecialEventDetail>> {
    log('Getting leisure special event detail');
    return execAction('leisure special event detail', this.requestBuilder.createGetSpecialEventDetailQuery(params));
  }

  specialEventList(): Promise<ResponseEither<ILeisureSpecialEventListPayload>> {
    log('Getting leisure special event list');
    return execAction('leisure special event list', this.requestBuilder.createGetSpecialEventListQuery());
  }

  /** 레저 리스트 -- GET `/leisure/tickets` */
  ticketList(params: ILeisureTicketListRequestParams): Promise<ResponseEither<readonly ITicket[]>> {
    log('Getting leisure ticket list');
    return execAction('leisure ticket list', this.requestBuilder.createGetTicketListQuery(params), noContentAsError);
  }

  /** 레저 리스트 v3 -- GET `/leisure/products` */
  products(params: ILeisureTicketListRequestParams): Promise<ResponseEither<ILeisureProductsPayload>> {
    log('Getting leisure product list');
    return execAction('leisure product list', this.requestBuilder.createGetProductsQuery(params), noContentAsError);
  }

  /** 사용처: 쿠폰함 -> 레저/렌터카 디테일페이지 */
  productsByIds: getProductsByIdsFn = (params) => {
    log('Getting leisure product list by ids');
    return execAction(
      'leisure product list by ids',
      this.requestBuilder.createGetProductsByIds(params),
      noContentAsError,
    );
  };

  title(): Promise<ResponseEither<ILeisureHomeTitlePayload>> {
    log('Getting leisure home title');
    return execAction('leisure title', this.requestBuilder.createGetHomeTitleQuery(), noContentAsError);
  }

  /** 브랜드관 */
  popularBrandList(): Promise<ResponseEither<ILeisurePopularBrandListPayload>> {
    log('Getting leisure popular brand list');
    return execAction('leisure popular brand list', this.requestBuilder.createGetPopularBrandListQuery());
  }

  brandCategoryList(): Promise<ResponseEither<LeisureBrandInterface.ICategory[]>> {
    log('Getting leisure category list');
    return execAction('leisure category list', this.requestBuilder.createGetBrandCategoryListQuery());
  }

  brandList(params: ILeisureBrandListRequestParams): Promise<ResponseEither<LeisureBrandInterface.IDetail[]>> {
    log('Getting leisure brand list');
    return execAction('leisure brand list', this.requestBuilder.createGetBrandListQuery(params));
  }

  brandDetail(params: ILeisureBrandDetailRequestParams): Promise<ResponseEither<LeisureBrandInterface.IDetail>> {
    log('Getting leisure brand detail');
    return execAction('leisure brand detail', this.requestBuilder.createGetBrandDetailQuery(params));
  }

  brandTickets(params: ILeisureBrandTicketsRequestParams): Promise<ResponseEither<ITicket[]>> {
    log('Getting leisure brand tickets');
    return execAction('leisure brand tickets', this.requestBuilder.createGetBrandTicketsQuery(params));
  }

  /** 국내숙소-레저 지역기반 크로스셀링 */
  regionCrossSellWidget(
    params: ILeisureCrossSellWidgetRequestParams,
  ): Promise<ResponseEither<ILeisureCrossSellWidgetPayload>> {
    log('Getting leisure region cross sell widget');
    return execAction('leisure region cross sell widget', this.requestBuilder.createGetCrossSellWidgetQuery(params));
  }

  regionCrossSellList(
    params: ILeisureCrossSellListRequestParams,
  ): Promise<ResponseEither<ILeisureCrossSellListPayload>> {
    log('Getting leisure region cross sell list');
    return execAction('leisure region cross sell list', this.requestBuilder.createGetCrossSellListQuery(params));
  }
}
